import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sparkles,
  Play,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Globe,
  Info,
  Loader2,
  Trash2,
  Briefcase,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import { useSite } from '@/contexts/SiteContext';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Service {
  id: string;
  name: string;
  description?: string;
  duration_minutes?: number;
  price_from_cents?: number;
  currency?: string;
  lang?: string;
  status?: string;
}

interface GeneratedQuestion {
  id: string;
  service_id: string;
  service_name: string;
  category_key: string;
  question: string;
  language: string;
  answer_type?: string;
  intent?: string;
  priority?: number;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ServiceQuestionGenerator() {
  const { siteId: routeSiteId } = useParams<{ siteId?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedSite } = useSite();

  const siteId = routeSiteId || selectedSite?.id;

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [questions, setQuestions] = useState<GeneratedQuestion[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // ── Load services ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (siteId) loadServices();
  }, [siteId]);

  useEffect(() => {
    if (siteId && selectedServiceId) {
      loadQuestions();
    } else {
      setQuestions([]);
    }
  }, [siteId, selectedServiceId]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const res = await authFetch(`/api/services/${siteId}?limit=500`);
      const data = await res.json();
      setServices(data.services || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load services',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Load existing questions for selected service ──────────────────────────

  const loadQuestions = async () => {
    if (!siteId || !selectedServiceId) return;
    setLoadingQuestions(true);
    try {
      const res = await authFetch(`/api/services/${siteId}/questions?limit=200`);
      const data = await res.json();
      // Filter to selected service
      const filtered = (data.questions || []).filter(
        (q: GeneratedQuestion) => q.service_id === selectedServiceId
      );
      setQuestions(filtered);
    } catch {
      // Non-critical - just show empty
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // ── Generate questions ────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!siteId) return;

    try {
      setGenerating(true);
      const body: Record<string, any> = {
        language: selectedLanguage,
        forceRegenerate: false,
      };

      if (selectedServiceId) {
        body.serviceId = selectedServiceId;
      }

      const res = await authFetch(`/api/services/${siteId}/generate-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (data.error) {
        throw new Error(data.error);
      }

      toast({
        title: 'Generation Complete',
        description: `Generated ${data.generated || data.total || 0} questions`,
      });

      // Reload questions
      if (selectedServiceId) {
        loadQuestions();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate questions',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  // ── Dismiss a question ────────────────────────────────────────────────────

  const handleDismiss = (questionId: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
    // Note: No backend delete endpoint yet — this is UI-only dismissal
  };

  // ── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!siteId) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>Select a site to generate service questions.</AlertDescription>
      </Alert>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Generate Service Questions</h1>
          <p className="text-muted-foreground mt-1">
            Generate characteristic questions for your services
          </p>
        </div>
      </div>

      {/* Info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Generate questions about what makes each service unique, what clients should know,
          and what differentiates it from competitors.
          {services.length === 0 && (
            <span className="block mt-1 font-medium">
              No services found. Import services first via the Service Inventory Import page.
            </span>
          )}
        </AlertDescription>
      </Alert>

      {/* Service Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Select Service
          </CardTitle>
          <CardDescription>
            Choose a specific service or generate for all services
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Service</Label>
              <Select
                value={selectedServiceId}
                onValueChange={setSelectedServiceId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All services</SelectItem>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {s.duration_minutes ? ` (${s.duration_minutes} min)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Language</Label>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish (Espanol)</SelectItem>
                  <SelectItem value="de">German (Deutsch)</SelectItem>
                  <SelectItem value="fr">French (Francais)</SelectItem>
                  <SelectItem value="it">Italian (Italiano)</SelectItem>
                  <SelectItem value="pt">Portuguese (Portugues)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleGenerate}
              disabled={generating || services.length === 0}
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Questions
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Generated Questions */}
      {(questions.length > 0 || loadingQuestions) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Generated Questions
              {questions.length > 0 && (
                <Badge variant="secondary">{questions.length}</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Questions generated for the selected service
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingQuestions ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {questions.map((q) => (
                  <div
                    key={q.id}
                    className="flex items-start justify-between border rounded-lg p-3 hover:bg-accent/50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{q.question}</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {q.category_key}
                        </Badge>
                        {q.intent && (
                          <Badge variant="secondary" className="text-xs">
                            {q.intent}
                          </Badge>
                        )}
                        {q.priority != null && (
                          <Badge variant="secondary" className="text-xs">
                            Priority: {q.priority}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 flex-shrink-0"
                      onClick={() => handleDismiss(q.id)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty state when a service is selected but no questions */}
      {selectedServiceId && questions.length === 0 && !loadingQuestions && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-8 w-8 text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-center">
              No questions generated yet for this service.
              <br />
              Click "Generate Questions" to create characteristic questions.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
